import { Injectable } from '@angular/core'
import * as msal from '@azure/msal-browser'
import { UserInfo } from './api/model/backend.model'
import { Subject, firstValueFrom } from 'rxjs'
import { Router } from '@angular/router'
import { AuthenticationResult } from '@azure/msal-browser'
import * as fromAuthActions from './core/store/auth/auth.actions'
import { Store } from '@ngrx/store'
import { AppState } from './core/store'
import { MatDialog } from '@angular/material/dialog'
import { NewUserDisclaimerDialogComponent } from './login/new-user-disclaimer-dialog.component'
import { BackendService } from './api/backend/backend.service'
import { MatSnackBar } from '@angular/material/snack-bar'
import { environment } from '../environments/environment'
import { LegacyApiService } from './legacy/services/legacy-api.service'

// based on this sample: https://learn.microsoft.com/en-us/azure/active-directory-b2c/configure-authentication-sample-spa-app

@Injectable({ providedIn: 'root' })
export class AuthService {
  private msal: msal.PublicClientApplication | null = null
  private userInfo: UserInfo | null = null
  private username: string | null = null
  private handledAfterLoginRedirect = false
  private accessToken = ''

  account: msal.AccountInfo | null = null
  accessTokenRefreshed = new Subject<string>()

  constructor(
    private router: Router,
    private store: Store<AppState>,
    public dialog: MatDialog,
    private backendService: BackendService,
    private _snackBar: MatSnackBar,
    private legacyApiService: LegacyApiService,
  ) {}

  // Called by APP_INITIALIZER before Angular's router starts, so that
  // window.location.search still contains the B2C auth code when handleRedirectPromise runs.
  // Also handles silent token restore from the MSAL sessionStorage cache on page refresh.
  async initOnAppStart(): Promise<void> {
    // UI test bypass: skip MSAL entirely and mark the session authenticated so that
    // Playwright can reach the MSAL-guarded legacy pages. Only enabled by the `ui-tests`
    // build configuration (environment.ui-tests.ts). A fake (unsigned) JWT is injected by
    // Playwright via window.__E2E_TOKEN__ so the app's AuthInterceptor sees a valid `exp`
    // and avoids redirecting/reloading during the suite.
    if (environment.e2eAuthBypass) {
      const e2eWindow = window as unknown as {
        __E2E_TOKEN__?: string
        __E2E_USERNAME__?: string
      }
      // No backend to ask for allowed roles under this bypass — grant access outright.
      this.store.dispatch(fromAuthActions.setAllowedRoles({ allowedRoles: ['e2e-bypass'] }))
      this.store.dispatch(
        fromAuthActions.identifySuccess({
          username: e2eWindow.__E2E_USERNAME__ ?? 'e2e@lockton.test',
          redirectURL: window.location.href,
          name: 'E2E Test User',
          token:
            e2eWindow.__E2E_TOKEN__ ??
            'eyJhbGciOiJub25lIiwidHlwIjoiSldUIn0=.eyJleHAiOjQxMDI0NDQ4MDB9.',
          roles: ['e2e-bypass'],
        }),
      )
      return
    }

    if (!environment.msal.clientId || environment.msal.clientId.startsWith('TODO')) {
      return
    }

    await this.initMsal()
    const redirectResult = await this.msal!.handleRedirectPromise()

    let account: msal.AccountInfo | null = null
    let accessToken: string | null = null

    if (redirectResult?.account && redirectResult.accessToken) {
      account = redirectResult.account
      accessToken = redirectResult.accessToken
    } else {
      const cached = this.msal!.getAllAccounts()
      if (cached.length > 0) {
        try {
          const silentResult = await this.msal!.acquireTokenSilent({
            account: cached[0],
            scopes: environment.msal.scopes as string[],
          })
          account = silentResult?.account ?? cached[0]
          accessToken = silentResult?.accessToken ?? null
        } catch {
          // Silent acquisition failed — fall through to loginRedirect below.
        }
      }
    }

    if (account && accessToken) {
      this.account = account
      this.accessToken = accessToken
      this.accessTokenRefreshed.next(accessToken)

      const claims = account.idTokenClaims as Record<string, unknown>
      const name = [claims?.['given_name'], claims?.['family_name']]
        .filter((v): v is string => typeof v === 'string')
        .join(' ')
      const roles = (claims?.['roles'] as string[] | undefined) ?? []

      try {
        const allowedRoles = await firstValueFrom(this.legacyApiService.getAllowedRoles())
        this.store.dispatch(fromAuthActions.setAllowedRoles({ allowedRoles }))
      } catch (error) {
        console.error('Failed to load allowed roles', error)
      }

      this.store.dispatch(
        fromAuthActions.identifySuccess({
          username: account.username,
          redirectURL: window.location.href,
          name,
          token: accessToken,
          roles,
        }),
      )
    } else {
      // No valid session — skip the login page and go straight to MSAL.
      // Store the intended path so redirectUponLoadSuccess$ can restore it after auth.
      sessionStorage.setItem('reDirectUrl', window.location.pathname)
      await this.loginRedirect()
    }
  }

  async loginRedirect(): Promise<void> {
    if (!this.msal) await this.initMsal()
    await this.msal!.loginRedirect({ scopes: environment.msal.scopes as string[] })
  }

  async signOut() {
    this.account = null
    this.username = null
    sessionStorage.removeItem('username')

    if (this.msal) {
      await this.msal.logoutRedirect({
        postLogoutRedirectUri: window.location.origin,
      })
    } else {
      console.log('An attempt was made to log out while MSAL was not initialized!')
    }
  }

  async trySignIn(username: string, force: boolean) {
    this.username = username
    if (this.username && this.username.trim()) {
      const normalizedEmail = this.username.trim().toLowerCase()
      sessionStorage.setItem('username', normalizedEmail)
      await this.initMsalForLogin(normalizedEmail, force)
    } else if (force) {
      alert('username is required')
    }
  }

  async handleAfterLoginRedirect(username: string | null) {
    this.username = username
    this.handledAfterLoginRedirect = true
    await this.initMsalForLogin(this.username, false)
  }

  private async initMsalForLogin(email: string | null, forceLogin: boolean) {
    await this.initMsal()
    const response = await this.msal!.handleRedirectPromise()

    if (response) {
      this.handledAfterLoginRedirect = true
      await this.setAccount(response.account!)
    }

    if (this.account === null) {
      // in case of page refresh
      await this.selectAccount()
    }

    if (this.account) {
      // tslint:disable-next-line: no-non-null-assertion
      this.userInfo = (await this.backendService.getUserInfo().toPromise())?.data!
      if (this.userInfo.approval_timestamp === null) {
        const result = await this.dialog
          .open(NewUserDisclaimerDialogComponent, {
            data: this.userInfo.eulaContent,
            disableClose: true,
          })
          .afterClosed()
          .toPromise()
        if (result === 'agree') {
          const currentDate = Date().toString().slice(0, 25)
          // tslint:disable-next-line
          const response = <any>(
            await this.backendService
              .putUserTimeStamp(this.userInfo.id, '"' + currentDate + '"')
              .toPromise()
          )
          if (response.error) {
            this._snackBar.open('Error Editing User login timestamp.', 'X', {
              duration: 2000,
            })
          } else {
            this.store.dispatch(
              fromAuthActions.identifySuccess({
                username: this.account.username,
                redirectURL: window.location.origin,
                name:
                  // tslint:disable-next-line
                  this.account.idTokenClaims!['given_name'] +
                  ' ' +
                  // tslint:disable-next-line
                  this.account.idTokenClaims!['family_name'],
                token: this.accessToken,
                roles:
                  ((this.account.idTokenClaims as Record<string, unknown>)?.[
                    'roles'
                  ] as string[]) ?? [],
              }),
            )
          }
        }
      } else {
        this.store.dispatch(
          fromAuthActions.identifySuccess({
            username: this.account.username,
            redirectURL: window.location.origin,
            name:
              // tslint:disable-next-line
              this.account.idTokenClaims!['given_name'] +
              ' ' +
              // tslint:disable-next-line
              this.account.idTokenClaims!['family_name'],
            token: this.accessToken,
            roles:
              ((this.account.idTokenClaims as Record<string, unknown>)?.['roles'] as string[]) ??
              [],
          }),
        )
      }
    } else if (forceLogin) {
      await this.msal!.loginRedirect({
        scopes: environment.msal.scopes as string[],
        loginHint: email ?? undefined,
      })
    }
  }

  private async initMsal(authority = environment.msal.authority) {
    this.msal = new msal.PublicClientApplication({
      auth: {
        authority,
        clientId: environment.msal.clientId,
        knownAuthorities: [environment.msal.knownAuthority],
        redirectUri: window.location.origin,
        navigateToLoginRequestUrl: false,
      },
      cache: {
        cacheLocation: 'sessionStorage',
        storeAuthStateInCookie: false,
      },
    })
    await this.msal.initialize()
  }

  private async selectAccount() {
    const currentAccounts = this.msal!.getAllAccounts()

    if (currentAccounts.length < 1) {
      return
    } else if (currentAccounts.length > 1) {
      // Due to the way MSAL caches account objects, the auth response from initiating a user-flow
      // is cached as a new account, resulting in more than one account in the cache. Select the
      // account whose homeAccountId matches the sign-in policy embedded in the authority URL.
      const policyName = environment.msal.authority.split('/').at(-1) ?? ''
      const accounts = currentAccounts.filter(
        (account) =>
          account.homeAccountId.toUpperCase().includes(policyName.toUpperCase()) &&
          account.idTokenClaims &&
          account.idTokenClaims.iss &&
          account.idTokenClaims.iss
            .toUpperCase()
            .includes(environment.msal.knownAuthority.toUpperCase()) &&
          account.idTokenClaims.aud === environment.msal.clientId,
      )

      if (accounts.length > 1) {
        // localAccountId identifies the entity for which the token asserts information.
        if (accounts.every((account) => account.localAccountId === accounts[0].localAccountId)) {
          // All accounts belong to the same user
          await this.setAccount(accounts[0])
        } else {
          // Multiple users detected. Logout all to be safe.
          await this.signOut()
        }
      } else if (accounts.length === 1) {
        await this.setAccount(accounts[0])
      }
    } else if (currentAccounts.length === 1) {
      await this.setAccount(currentAccounts[0])
    }
  }

  private async setAccount(account: msal.AccountInfo) {
    const navigateToHome =
      this.account === null && account !== null && this.handledAfterLoginRedirect
    this.account = account
    this.username = account.username
    sessionStorage.setItem('username', account.username) // in case user came from password reset flow
    await this.getAccessToken()

    if (navigateToHome) {
      await this.router.navigate(['/'])
    }
  }

  async getAccessToken(forceRefresh: boolean = false): Promise<string | null> {
    if (this.account === null) {
      return null
    }

    const account = this.msal!.getAccountByHomeId(this.account.homeAccountId)!

    const req = {
      account,
      scopes: environment.msal.scopes as string[],
      forceRefresh,
    }

    let response: AuthenticationResult | null = null

    try {
      response = await this.msal!.acquireTokenSilent(req)
    } catch (error) {
      console.log('Silent access token acquisition failed.')
      console.error(error)
    }

    if (response === null || !response.accessToken || response.accessToken === '') {
      console.log('Acquiring access token using redirect...')
      await this.msal!.acquireTokenRedirect(req)
      return ''
    } else {
      const hasChanged = this.accessToken !== response.accessToken

      if (hasChanged) {
        this.accessToken = response.accessToken
        this.accessTokenRefreshed.next(this.accessToken)
      }

      return this.accessToken
    }
  }
}
